import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getWidgetConfig, updateWidgetConfig, toggleWidget, getBillingStatus, type BillingStatus } from '../api';
import { getShopDomain } from '../shopify';
import '../widgets/hype-announcement-bar.js';

// Per-announcement item shape
interface AnnouncementItem {
  text: string;
  link: string;
  start: string;
  end: string;
}

// Form state shape — mirrors the backend config fields exactly
interface AnnouncementBarConfig {
  announcements: AnnouncementItem[];
  rotation_interval: number;
  placement: string;
  show_close: boolean;
  bg_color: string;
  text_color: string;
  accent_color: string;
  background_style: string;
  gradient_end_color: string;
  size: string;
  animation_style: string;
}

interface StylePreset {
  id: string;
  name: string;
  description: string;
  bg_color: string;
  text_color: string;
  accent_color: string;
  background_style?: string;
  gradient_end_color?: string;
}

const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'shopify-green',
    name: 'Shopify Green',
    description: 'Classic Shopify green with white text',
    bg_color: '#008060',
    text_color: '#ffffff',
    accent_color: '#c9ffc9',
  },
  {
    id: 'dark-contrast',
    name: 'Dark Contrast',
    description: 'Dark background with orange accent',
    bg_color: '#1a1a1a',
    text_color: '#f5f5f5',
    accent_color: '#ff6b35',
  },
  {
    id: 'sale-red',
    name: 'Sale Red',
    description: 'Red background with yellow accent',
    bg_color: '#dc2626',
    text_color: '#ffffff',
    accent_color: '#facc15',
  },
  {
    id: 'neutral-light',
    name: 'Neutral Light',
    description: 'Light background with blue accent',
    bg_color: '#f5f5f5',
    text_color: '#333333',
    accent_color: '#005bd3',
  },
  {
    id: 'holiday',
    name: 'Holiday',
    description: 'Forest green with gold accent',
    bg_color: '#166534',
    text_color: '#ffffff',
    accent_color: '#d4a853',
  },
];

const DEFAULTS: AnnouncementBarConfig = {
  announcements: [],
  rotation_interval: 5,
  placement: 'top',
  show_close: true,
  bg_color: '#008060',
  text_color: '#ffffff',
  accent_color: '#c9ffc9',
  background_style: 'solid',
  gradient_end_color: '#004d3a',
  size: 'small',
  animation_style: 'fade',
};

type PreviewState = 'single' | 'rotating' | 'with-close';

@customElement('hype-announcement-bar-page')
export class AnnouncementBarPage extends LitElement {
  override createRenderRoot() { return this; }

  // Loading / saving states
  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _loadError = '';
  @state() private _saveSuccess = false;
  @state() private _saveError = '';
  @state() private _saveFlash = false;

  private _flashTimer: ReturnType<typeof setTimeout> | undefined;

  // Widget enabled toggle
  @state() private _enabled = false;

  // Preview state override — does not affect saved config
  @state() private _previewState: PreviewState = 'single';

  // Announcements list
  @state() private _announcements: AnnouncementItem[] = [];

  // Form fields
  @state() private _rotation_interval = DEFAULTS.rotation_interval;
  @state() private _placement = DEFAULTS.placement;
  @state() private _show_close = DEFAULTS.show_close;
  @state() private _bg_color = DEFAULTS.bg_color;
  @state() private _text_color = DEFAULTS.text_color;
  @state() private _accent_color = DEFAULTS.accent_color;
  @state() private _background_style = DEFAULTS.background_style;
  @state() private _gradient_end_color = DEFAULTS.gradient_end_color;
  @state() private _size = DEFAULTS.size;
  @state() private _animation_style = DEFAULTS.animation_style;

  // Custom CSS (Pro plan)
  @state() private _custom_css = '';
  @state() private _billing: BillingStatus | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadConfig();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this._flashTimer);
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._loadError = '';
    try {
      const [widget, billing] = await Promise.all([
        getWidgetConfig('announcement_bar'),
        getBillingStatus(),
      ]);
      this._billing = billing;
      this._enabled = widget.enabled;
      this._custom_css = widget.custom_css ?? '';
      const c = widget.config as Partial<AnnouncementBarConfig>;
      const rawAnnouncements = (c.announcements ?? []) as AnnouncementItem[];
      this._announcements = rawAnnouncements.map((item) => ({
        text: item.text ?? '',
        link: item.link ?? '',
        start: item.start ?? '',
        end: item.end ?? '',
      }));
      this._rotation_interval = Number(c.rotation_interval ?? DEFAULTS.rotation_interval);
      this._placement         = (c.placement   ?? DEFAULTS.placement)   as string;
      this._show_close        = Boolean(c.show_close ?? DEFAULTS.show_close);
      this._bg_color          = (c.bg_color    ?? DEFAULTS.bg_color)    as string;
      this._text_color        = (c.text_color  ?? DEFAULTS.text_color)  as string;
      this._accent_color      = (c.accent_color ?? DEFAULTS.accent_color) as string;
      this._background_style   = (c.background_style   ?? DEFAULTS.background_style)   as string;
      this._gradient_end_color = (c.gradient_end_color ?? DEFAULTS.gradient_end_color) as string;
      this._size               = (c.size               ?? DEFAULTS.size)               as string;
      this._animation_style    = (c.animation_style    ?? DEFAULTS.animation_style)    as string;
    } catch (err) {
      this._loadError = err instanceof Error ? err.message : 'Failed to load configuration.';
    } finally {
      this._loading = false;
    }
  }

  private async _handleToggle(enabled: boolean): Promise<void> {
    this._enabled = enabled;
    try {
      const config: Record<string, unknown> = {
        announcements: this._announcements,
        rotation_interval: this._rotation_interval,
        placement: this._placement,
        show_close: this._show_close,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
        background_style: this._background_style,
        gradient_end_color: this._gradient_end_color,
        size: this._size,
        animation_style: this._animation_style,
      };
      await toggleWidget('announcement_bar', enabled, config);
    } catch (err) {
      // Revert optimistic update on failure
      this._enabled = !enabled;
      this._saveError = err instanceof Error ? err.message : 'Failed to toggle widget.';
      this._saveSuccess = false;
    }
  }

  private async _handleSave(): Promise<void> {
    this._saving = true;
    this._saveSuccess = false;
    this._saveError = '';
    try {
      const config: AnnouncementBarConfig = {
        announcements: this._announcements,
        rotation_interval: this._rotation_interval,
        placement: this._placement,
        show_close: this._show_close,
        bg_color: this._bg_color,
        text_color: this._text_color,
        accent_color: this._accent_color,
        background_style: this._background_style,
        gradient_end_color: this._gradient_end_color,
        size: this._size,
        animation_style: this._animation_style,
      };
      await updateWidgetConfig('announcement_bar', config as unknown as Record<string, unknown>, this._custom_css);
      this._saveSuccess = true;
      // Trigger brief success flash on save button + preview pulse
      clearTimeout(this._flashTimer);
      this._saveFlash = true;
      this._flashTimer = setTimeout(() => { this._saveFlash = false; }, 1500);
    } catch (err) {
      this._saveError = err instanceof Error ? err.message : 'Failed to save configuration.';
    } finally {
      this._saving = false;
    }
  }

  private _handleColor(key: string, value: string): void {
    if (key === 'bg_color')            this._bg_color            = value;
    if (key === 'text_color')          this._text_color          = value;
    if (key === 'accent_color')        this._accent_color        = value;
    if (key === 'gradient_end_color')  this._gradient_end_color  = value;
  }

  private _applyPreset(preset: StylePreset): void {
    this._bg_color = preset.bg_color;
    this._text_color = preset.text_color;
    this._accent_color = preset.accent_color;
    this._background_style = preset.background_style ?? 'solid';
    this._gradient_end_color = preset.gradient_end_color ?? '#004d3a';
  }

  // Announcement list mutation helpers
  private _addAnnouncement(): void {
    const item: AnnouncementItem = { text: '', link: '', start: '', end: '' };
    this._announcements = [...this._announcements, item];
  }

  private _removeAnnouncement(index: number): void {
    this._announcements = this._announcements.filter((_, i) => i !== index);
  }

  private _updateAnnouncement(index: number, field: keyof AnnouncementItem, value: string): void {
    const updated = [...this._announcements];
    updated[index] = { ...updated[index], [field]: value };
    this._announcements = updated;
  }

  private _renderAnnouncementCard(item: AnnouncementItem, index: number) {
    return html`
      <s-box padding="base" borderWidth="base" borderColor="border" borderRadius="base">
        <s-stack gap="small">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            <s-text>Announcement ${index + 1}</s-text>
            <s-button
              variant="tertiary"
              tone="critical"
              @click=${() => this._removeAnnouncement(index)}
            >Remove</s-button>
          </s-stack>

          <s-text-field
            label="Message"
            required
            value=${item.text}
            @change=${(e: Event) => this._updateAnnouncement(index, 'text', (e.currentTarget as HTMLInputElement).value)}
          ></s-text-field>

          <s-text-field
            label="Link URL"
            type="url"
            placeholder="https://..."
            value=${item.link}
            @change=${(e: Event) => this._updateAnnouncement(index, 'link', (e.currentTarget as HTMLInputElement).value)}
          ></s-text-field>

          <s-grid gridTemplateColumns="'1fr 1fr'" gap="base">
            <s-text-field
              label="Start date"
              type="datetime-local"
              help-text="Leave blank to show immediately"
              value=${item.start}
              @change=${(e: Event) => this._updateAnnouncement(index, 'start', (e.currentTarget as HTMLInputElement).value)}
            ></s-text-field>

            <s-text-field
              label="End date"
              type="datetime-local"
              help-text="Leave blank to show indefinitely"
              value=${item.end}
              @change=${(e: Event) => this._updateAnnouncement(index, 'end', (e.currentTarget as HTMLInputElement).value)}
            ></s-text-field>
          </s-grid>
        </s-stack>
      </s-box>
    `;
  }

  /** Returns the preview widget based on _previewState. */
  private _renderPreview() {
    const sampleAnnouncements = this._announcements.length > 0
      ? this._announcements
      : [
          { text: 'Free shipping on orders over $50!', link: '', start: '', end: '' },
          { text: 'Summer sale \u2014 20% off everything', link: '', start: '', end: '' },
        ];

    let previewAnnouncements: AnnouncementItem[];
    let showClose: string;

    switch (this._previewState) {
      case 'single':
        previewAnnouncements = [sampleAnnouncements[0]];
        showClose = 'false';
        break;
      case 'rotating':
        previewAnnouncements = sampleAnnouncements.slice(0, 2);
        showClose = 'false';
        break;
      case 'with-close':
        previewAnnouncements = [sampleAnnouncements[0]];
        showClose = 'true';
        break;
    }

    const announcementsJson = JSON.stringify(previewAnnouncements);
    const minH = this._size === 'large' ? '64' : this._size === 'medium' ? '52' : '44';

    return html`
      <div style="transform:translateZ(0);overflow:hidden;min-height:${minH}px;">
        <hype-announcement-bar
          announcements=${announcementsJson}
          rotation-interval=${String(this._rotation_interval)}
          placement="top"
          bg-color=${this._bg_color}
          text-color=${this._text_color}
          accent-color=${this._accent_color}
          show-close=${showClose}
          background-style=${this._background_style}
          gradient-end-color=${this._gradient_end_color}
          size=${this._size}
          animation-style=${this._animation_style}
        ></hype-announcement-bar>
      </div>
    `;
  }

  override render() {
    if (this._loading) {
      return html`<s-spinner></s-spinner>`;
    }

    if (this._loadError) {
      return html`
        <s-page heading="Announcement Bar">
          <s-banner tone="critical">
            <strong>Failed to load configuration</strong><br />
            ${this._loadError}
          </s-banner>
          <s-button @click=${() => void this._loadConfig()}>Retry</s-button>
        </s-page>
      `;
    }

    const atMax = this._announcements.length >= 5;

    const isPresetActive = (preset: StylePreset) =>
      preset.bg_color === this._bg_color &&
      preset.text_color === this._text_color &&
      preset.accent_color === this._accent_color &&
      (preset.background_style ?? 'solid') === this._background_style &&
      (preset.gradient_end_color ?? '#004d3a') === this._gradient_end_color;

    return html`
      <style>
        hype-announcement-bar-page .preview-layout {
          display: grid;
          grid-template-columns: 55fr 45fr;
          gap: 24px;
          align-items: start;
        }
        hype-announcement-bar-page .config-column {
          display: flex;
          flex-direction: column;
          gap: var(--p-space-base, 16px);
        }
        hype-announcement-bar-page .preview-panel {
          position: sticky;
          top: 80px;
        }
        hype-announcement-bar-page .preview-frame {
          border: 1px solid #e3e3e3;
          border-radius: 12px;
          background: #fafafa;
          padding: 24px;
          overflow: hidden;
        }
        hype-announcement-bar-page .save-btn-success {
          background-color: #008060 !important;
          border-color: #008060 !important;
          transition: background-color 0.2s, border-color 0.2s;
        }
        @keyframes hype-preview-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(0,128,96,0.5); border-color: #008060; }
          60%  { box-shadow: 0 0 0 6px rgba(0,128,96,0); border-color: #008060; }
          100% { box-shadow: 0 0 0 0 rgba(0,128,96,0); border-color: #e3e3e3; }
        }
        hype-announcement-bar-page .preview-frame.preview-pulse {
          animation: hype-preview-pulse 1s ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          hype-announcement-bar-page .save-btn-success {
            transition: none;
          }
          hype-announcement-bar-page .preview-frame.preview-pulse {
            animation: none;
            border-color: #008060;
          }
        }
        hype-announcement-bar-page .preview-states {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }
        hype-announcement-bar-page .preview-state-btn {
          padding: 4px 8px;
          font-size: 11px;
          border: 1px solid #ccc;
          border-radius: 4px;
          background: #fff;
          cursor: pointer;
        }
        hype-announcement-bar-page .preview-state-btn.active {
          background: #005bd3;
          color: #fff;
          border-color: #005bd3;
        }
        hype-announcement-bar-page .preset-grid {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        hype-announcement-bar-page .preset-card {
          width: 120px;
          cursor: pointer;
          border: 2px solid #e3e3e3;
          border-radius: 8px;
          padding: 10px;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        hype-announcement-bar-page .preset-card:hover {
          border-color: #b5b5b5;
          box-shadow: 0 2px 6px rgba(0,0,0,0.10);
        }
        hype-announcement-bar-page .preset-card.active {
          border-color: #005bd3;
          box-shadow: 0 0 0 1px #005bd3;
          background: #f0f7ff;
        }
        hype-announcement-bar-page .preset-card-name {
          font-size: 12px;
          font-weight: 600;
          color: #303030;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        hype-announcement-bar-page .preset-swatches {
          display: flex;
          gap: 3px;
          justify-content: center;
          margin-bottom: 3px;
        }
        hype-announcement-bar-page .preset-swatch {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.15);
        }
        hype-announcement-bar-page .preset-card-style {
          font-size: 10px;
          color: #666;
          text-transform: capitalize;
        }
        hype-announcement-bar-page .preview-expired-placeholder {
          text-align: center;
          padding: 32px 16px;
          color: #666;
          font-size: 13px;
          border: 1px dashed #ccc;
          border-radius: 8px;
        }
        @media (max-width: 768px) {
          hype-announcement-bar-page .preview-layout {
            grid-template-columns: 1fr;
          }
          hype-announcement-bar-page .preview-panel {
            position: static;
          }
        }
      </style>
      <s-page heading="Announcement Bar">
        <s-button
          slot="primary-action"
          variant="primary"
          class="${this._saveFlash ? 'save-btn-success' : ''}"
          ?loading=${this._saving}
          @click=${() => void this._handleSave()}
        >${this._saveFlash ? '✓ Saved' : 'Save'}</s-button>
        ${(() => { const shop = getShopDomain(); return shop ? html`<s-button slot="secondary-actions" variant="tertiary" @click=${() => window.open(`https://${shop}`, '_blank')} aria-label="Preview widget on your live store">Preview on store</s-button>` : ''; })()}

        ${this._saveSuccess ? html`
          <s-banner
            tone="success"
            dismissible
            @dismiss=${() => { this._saveSuccess = false; }}
          >Configuration saved successfully.</s-banner>
        ` : ''}

        ${this._saveError ? html`
          <s-banner
            tone="critical"
            dismissible
            @dismiss=${() => { this._saveError = ''; }}
          >${this._saveError}</s-banner>
        ` : ''}

        <s-section heading="Enable">
          <s-checkbox
            label="Enable widget"
            ?checked=${this._enabled}
            @change=${(e: Event) => void this._handleToggle((e.currentTarget as HTMLInputElement).checked)}
          ></s-checkbox>
        </s-section>

        <div class="preview-layout">
          <div class="config-column">
            <s-section heading="Quick Start" class="${this._enabled ? '' : 'disabled-overlay'}">
              <div class="preset-grid">
                ${STYLE_PRESETS.map(preset => html`
                  <div
                    class="preset-card ${isPresetActive(preset) ? 'active' : ''}"
                    role="button"
                    tabindex="0"
                    @click=${() => this._applyPreset(preset)}
                    @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._applyPreset(preset); } }}
                    title=${preset.description}
                  >
                    <div class="preset-card-name">${preset.name}</div>
                    <div class="preset-swatches">
                      <div class="preset-swatch" style="background:${preset.bg_color}"></div>
                      <div class="preset-swatch" style="background:${preset.text_color}"></div>
                      <div class="preset-swatch" style="background:${preset.accent_color}"></div>
                    </div>
                  </div>
                `)}
              </div>
            </s-section>

            <s-section heading="Announcements" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                ${this._announcements.map((item, index) => this._renderAnnouncementCard(item, index))}

                <s-button
                  variant="secondary"
                  ?disabled=${atMax}
                  @click=${() => this._addAnnouncement()}
                >Add announcement</s-button>
                <s-text tone="subdued" variant="bodySm">Up to 5 announcements. Active announcements rotate automatically.</s-text>
              </s-stack>
            </s-section>

            <s-section heading="Behavior" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-text-field
                  type="number"
                  label="Rotation interval"
                  help-text="Seconds between announcements."
                  min="3"
                  max="30"
                  step="1"
                  value=${String(this._rotation_interval)}
                  @change=${(e: Event) => { this._rotation_interval = Number((e.currentTarget as HTMLInputElement).value); }}
                ></s-text-field>

                <s-select
                  label="Placement"
                  value=${this._placement}
                  @change=${(e: Event) => { this._placement = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="top">Top of page</s-option>
                  <s-option value="bottom">Bottom of page</s-option>
                </s-select>

                <s-checkbox
                  label="Show close button"
                  ?checked=${this._show_close}
                  @change=${(e: Event) => { this._show_close = (e.currentTarget as HTMLInputElement).checked; }}
                ></s-checkbox>

                <s-select
                  label="Size"
                  value=${this._size}
                  @change=${(e: Event) => { this._size = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="small">Small</s-option>
                  <s-option value="medium">Medium</s-option>
                  <s-option value="large">Large</s-option>
                </s-select>

                <s-select
                  label="Animation style"
                  value=${this._animation_style}
                  @change=${(e: Event) => { this._animation_style = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="fade">Fade</s-option>
                  <s-option value="slide">Slide</s-option>
                  <s-option value="none">None</s-option>
                </s-select>
              </s-stack>
            </s-section>

            <s-section heading="Appearance" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                <s-select
                  label="Background style"
                  value=${this._background_style}
                  @change=${(e: Event) => { this._background_style = (e.currentTarget as HTMLInputElement).value; }}
                >
                  <s-option value="solid">Solid</s-option>
                  <s-option value="gradient">Gradient</s-option>
                  <s-option value="glass">Glassmorphism</s-option>
                </s-select>

                <s-stack gap="base">
                  <s-text>${this._background_style === 'gradient' ? 'Gradient start color' : 'Background'}</s-text>
                  <s-color-picker
                    value=${this._bg_color}
                    @input=${(e: Event) => this._handleColor('bg_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>

                ${this._background_style === 'gradient' ? html`
                  <s-stack gap="base">
                    <s-text>Gradient end color</s-text>
                    <s-color-picker
                      value=${this._gradient_end_color}
                      @input=${(e: Event) => this._handleColor('gradient_end_color', (e.currentTarget as HTMLInputElement).value)}
                    ></s-color-picker>
                  </s-stack>
                ` : ''}

                <s-stack gap="base">
                  <s-text>Text</s-text>
                  <s-color-picker
                    value=${this._text_color}
                    @input=${(e: Event) => this._handleColor('text_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>

                <s-stack gap="base">
                  <s-text>Link / CTA</s-text>
                  <s-color-picker
                    value=${this._accent_color}
                    @input=${(e: Event) => this._handleColor('accent_color', (e.currentTarget as HTMLInputElement).value)}
                  ></s-color-picker>
                </s-stack>
              </s-stack>
            </s-section>

            <s-section heading="Custom CSS" class="${this._enabled ? '' : 'disabled-overlay'}">
              <s-stack gap="base">
                ${this._billing && !(this._billing.features as Record<string, boolean>).custom_css ? html`
                  <s-banner tone="info">
                    Custom CSS is available on the Pro plan.
                    <a href="/billing">Upgrade to Pro</a> to inject custom styles.
                  </s-banner>
                ` : ''}
                <textarea
                  rows="6"
                  placeholder=".bar { border-radius: 8px; }"
                  .value=${this._custom_css}
                  ?disabled=${!!(this._billing && !(this._billing.features as Record<string, boolean>).custom_css)}
                  @input=${(e: Event) => { this._custom_css = (e.currentTarget as HTMLTextAreaElement).value; }}
                  style="width:100%;font-family:monospace;font-size:13px;padding:8px;border:1px solid #d2d5d8;border-radius:8px;resize:vertical;box-sizing:border-box;"
                ></textarea>
                <s-text tone="subdued" variant="bodySm">
                  CSS is scoped to this widget's Shadow DOM. Use class names like .bar, .announcement-text, .close-btn.
                </s-text>
              </s-stack>
            </s-section>
          </div>

          <div class="preview-panel">
            <s-section heading="Preview">
              <div class="preview-states">
                <s-button
                  size="slim"
                  variant=${this._previewState === 'single' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'single'; }}
                >Single</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'rotating' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'rotating'; }}
                >Rotating</s-button>
                <s-button
                  size="slim"
                  variant=${this._previewState === 'with-close' ? 'primary' : 'secondary'}
                  @click=${() => { this._previewState = 'with-close'; }}
                >With close button</s-button>
              </div>
              <div class="preview-frame ${this._saveFlash ? 'preview-pulse' : ''}">
                ${this._renderPreview()}
              </div>
            </s-section>
          </div>
        </div>
      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-announcement-bar-page': AnnouncementBarPage;
  }
}
